// Utility for tracking field changes and sending only modified data

// frontend/src/utils/changeTracker.js (this file)
// React import for the hook
import { useState } from 'react';

/**
 * Gets the fields that have changed between original and current values
 * @param {Object} originalValues - Original values when form was loaded
 * @param {Object} currentValues - Current form values
 * @param {Array} excludeFields - Fields to exclude from comparison (optional)
 * @returns {Object} Object containing only the changed fields
 */
export const getChangedFields = (originalValues, currentValues, excludeFields = []) => {
  const changedFields = {};
  
  // Get all unique keys from both objects
  const allKeys = new Set([
    ...Object.keys(originalValues || {}),
    ...Object.keys(currentValues || {})
  ]);
  
  allKeys.forEach(key => {
    // Skip excluded fields
    if (excludeFields.includes(key)) {
      return;
    }
    
    const originalValue = originalValues?.[key];
    const currentValue = currentValues?.[key];
    
    // Handle different value types
    if (isValueChanged(originalValue, currentValue)) {
      changedFields[key] = currentValue;
    }
  });
  
  return changedFields;
};

/**
 * Checks if a value has changed, handling different data types
 * @param {*} originalValue - Original value
 * @param {*} currentValue - Current value
 * @returns {boolean} True if values are different
 */
const isValueChanged = (originalValue, currentValue) => {
  // Handle null/undefined cases
  if (originalValue === null || originalValue === undefined) {
    return currentValue !== null && currentValue !== undefined;
  }
  
  if (currentValue === null || currentValue === undefined) {
    return originalValue !== null && originalValue !== undefined;
  }
  
  // Handle boolean values
  if (typeof originalValue === 'boolean' || typeof currentValue === 'boolean') {
    return Boolean(originalValue) !== Boolean(currentValue);
  }
  
  // Handle numbers
  if (typeof originalValue === 'number' || typeof currentValue === 'number') {
    return Number(originalValue) !== Number(currentValue);
  }
  
  // Handle dates
  if (originalValue instanceof Date || currentValue instanceof Date) {
    return new Date(originalValue).getTime() !== new Date(currentValue).getTime();
  }
  
  // Handle strings and other types
  return String(originalValue) !== String(currentValue);
};

/**
 * Merges multiple objects and returns only the changed fields
 * @param {Object} originalValues - Original values
 * @param {...Object} currentObjects - Current value objects to merge
 * @returns {Object} Object containing only changed fields
 */
export const getMergedChangedFields = (originalValues, ...currentObjects) => {
  const mergedCurrent = Object.assign({}, ...currentObjects);
  return getChangedFields(originalValues, mergedCurrent);
};

/**
 * Hook for managing change tracking in React components
 * @param {Object} initialValues - Initial values to track changes against
 * @returns {Object} Object with utilities for change tracking
 */
export const useChangeTracker = (initialValues) => {
  const [originalValues, setOriginalValues] = useState(initialValues);
  
  const updateOriginalValues = (newValues) => {
    setOriginalValues(newValues);
  };
  
  const getChangedFieldsFromCurrent = (...currentObjects) => {
    return getMergedChangedFields(originalValues, ...currentObjects);
  };
  
  const hasChanges = (...currentObjects) => {
    const changedFields = getChangedFieldsFromCurrent(...currentObjects);
    return Object.keys(changedFields).length > 0;
  };
  
  return {
    originalValues,
    updateOriginalValues,
    getChangedFields: getChangedFieldsFromCurrent,
    hasChanges
  };
};

